import React, { useState } from 'react';
import axios from 'axios';
import { Container, TextField, Button, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert, CircularProgress } from '@mui/material';
import { CountryDropdown } from 'react-country-region-selector';
import { ADMIN_BASE_URL } from '../config/config';
import { useAdminUserList } from '../hooks/useAdminUserList';
import '../components/App.css';

const AdminMessage = () => {
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [sendMessageError, setSendMessageError] = useState('');
  const [sendMessageSuccess, setSendMessageSuccess] = useState('');
  const {
    country,
    setCountry,
    id,
    setId,
    users,
    loadingUsers,
    loadedFromCache,
    applyFilters,
    refreshUsers,
  } = useAdminUserList();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('care_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleChangeMessage = (event) => {
    setMessage(event.target.value);
  };

  const handleChangeCountry = (val) => {
    setCountry(val);
  };

  const handleChangeId = (event) => {
    setId(event.target.value);
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      setSendMessageError('Please enter a message before sending.');
      setSendMessageSuccess('');
      return;
    }

    setSendingMessage(true);
    setSendMessageError('');
    setSendMessageSuccess('');

    try {
      let target_type = 'all';
      let target = '';

      if (country) {
        target_type = 'country';
        target = country;
      } else if (id) {
        target_type = 'user';
        target = id;
      }

      const response = await axios.post(
        `${ADMIN_BASE_URL}/message`,
        {
          message: message.trim(),
          target,
          target_type,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      setMessage('');
      setSendMessageSuccess(response.data?.msg || 'Message sent successfully.');
    } catch (error) {
      console.error('Error sending message:', error);
      setSendMessageError(
        error.response?.data?.error ||
          error.response?.data?.msg ||
          'Error sending message. Please try again.'
      );
    } finally {
      setSendingMessage(false);
    }
  };

  return (
    <Container>
        <Typography variant="h4" className="admin-message-title" gutterBottom>
          Send Message to Users
        </Typography>
        <Box className="admin-message-form-container">
          <CountryDropdown
            value={country}
            onChange={handleChangeCountry}
            labelType="full"
            className="admin-country-dropdown"
          />
          <TextField
            id="outlined-basic"
            label="User ID"
            fullWidth
            value={id}
            onChange={handleChangeId}
            size='small'
            variant="outlined"
            className="admin-user-id-input"
            style={{ marginBottom: '1rem'}}
          />
          <Button
            variant="contained"
            color="success"
            onClick={applyFilters}
            className="admin-filter-button"
            disabled={loadingUsers}
          >
            {loadingUsers ? 'Loading...' : 'Filter'}
          </Button>
        </Box>
        <div style={{ textAlign: 'center' }}>
        <TextField
          id="outlined-multiline-static"
          label="Message"
          multiline
          rows={4}
          fullWidth
          value={message}
          onChange={handleChangeMessage}
          variant="outlined"
          className="admin-message-input"
          style={{ marginBottom: '1rem'}}
          disabled={sendingMessage}
        />
        </div>
        <div style={{ textAlign: 'center' }}>
        <Button
          variant="contained"
          color="success"
          onClick={handleSendMessage}
          className="admin-send-message-button"
          disabled={sendingMessage}
          startIcon={sendingMessage ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {sendingMessage ? 'Sending...' : 'Send Message'}
        </Button>
        </div>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            color="success"
            onClick={refreshUsers}
            disabled={loadingUsers}
          >
            Refresh
          </Button>
        </Box>
        {loadedFromCache && !loadingUsers && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Showing cached user list. Click Refresh for the latest data.
          </Alert>
        )}
        {sendMessageSuccess && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {sendMessageSuccess}
          </Alert>
        )}
        {sendMessageError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {sendMessageError}
          </Alert>
        )}
        <TableContainer component={Paper} style={{marginTop: '2rem'}}>
        <Table>
  <TableHead>
    <TableRow>
      <TableCell>Name</TableCell>
      <TableCell>Email</TableCell>
      <TableCell>User ID</TableCell>
    </TableRow>
  </TableHead>
  <TableBody>
    {loadingUsers ? (
      <TableRow>
        <TableCell colSpan={3} align="center">Loading users...</TableCell>
      </TableRow>
    ) : users.length > 0 ? (
      users.map((user) => (
        <TableRow key={user._id}>
          <TableCell>{`${user.fName} ${user.lName}`}</TableCell>
          <TableCell>{user.email}</TableCell>
          <TableCell>{user._id}</TableCell>
        </TableRow>
      ))
    ) : (
      <TableRow>
        <TableCell colSpan={3} align="center">No users found</TableCell>
      </TableRow>
    )}
  </TableBody>
</Table>
        </TableContainer>
    </Container>
  );
};

export default AdminMessage;
